<template>
  <location-targeting-details-core
    ref="locationTargetingDetailsCore"
    :entity="entity"
    :excluded-creative-orientations="excludedCreativeOrientations"
    :excluded-creative-formats="excludedCreativeFormats"
    :excluded-iab-categories="excludedIabCategories"
    :excluded-languages="excludedLanguages"
    :excluded-locations="excludedLocations"
    :excluded-sites="excludedSites"
    :excluded-units="excludedUnits"
    :excluded-markets="excludedMarkets"
    :excluded-media-types="excludedMediaTypes"
    :excluded-networks="excludedNetworks"
    :excluded-geofence-groups="excludedGeofenceGroups"
    :excluded-unit-packs="excludedUnitPacks"
    :excluded-unit-tags="excludedUnitTags"
    :geofence-groups="geofenceGroups"
    :has-access-to-targeted-geofence-groups="hasAccessToTargetedGeofenceGroups"
    :creative-orientations="creativeOrientations"
    :creative-formats="creativeFormats"
    :iab-categories="iabCategories"
    :languages="languages"
    :locations="locations"
    :markets="markets"
    :media-types="mediaTypes"
    :selected-networks="selectedNetworks"
    :sites="sites"
    :targeted-unit-i-ds="targetedUnitIDs"
    :targeted-site-i-ds="targetedSiteIDs"
    :units="units"
    :unit-packs="unitPacks"
    :unit-tags="unitTags"
    :physical-screen-count="physicalScreenCount"
    :unit-geofences-to-export="unitGeofencesToExport"
    :site-geofences-to-export="siteGeofencesToExport"
    :is-audience-info-loading="isAudienceInfoLoading"
    :is-audience-with-geofences-info-loading="isAudienceWithGeofencesInfoLoading"
    :page-sizes="pageSizes"
    :page-numbers="pageNumbers"
    :lengths="lengths"
    :is-shared-plan="isSharedPlan"
    :is-shared-plan-preview-enabled="isSharedPlanPreviewEnabled"
    :are-geofences-created-less-than-5-min="areGeofencesCreatedLessThan5Min"
    @export-sites="exportSites"
    @export-units="exportUnits">
    <template #pane-header-right>
      <slot name="pane-header-right" />
    </template>
  </location-targeting-details-core>
</template>

<script>
  import { DateTime } from 'luxon';
  import { defer } from 'rxjs';
  import { mapActions } from 'vuex';

  import { Creative, Network } from '@/common/models';
  import CreativeFormatOptions from '@/common/models/CreativeFormatOptions';
  import CreativeOrientationOptions from '@/common/models/CreativeOrientationOptions';
  import IABCategories from '@/common/models/iab-category/IABCategories';
  import LineItem from '@/common/models/lineitem/LineItem';
  import Plan from '@/common/models/plan/Plan';
  import { Filter, FilterCollection, FilterComparisonOperator, QueryParams } from '@/common/models/query-params';
  import { useNetworks } from '@/org-app/services/tanstack/queries';
  import { waitForPendingQueries } from '@/org-app/services/tanstack/queryUtils';
  import pollWhile from '@/utils/PollWhileHelper';
  import UserNotifications from '@/utils/UserNotifications';
  import i18n from '@/vueI18n';

  import GeofenceSiteExportModel from './GeofenceSiteExportModel';
  import GeofenceUnitExportModel from './GeofenceUnitExportModel';
  import LocationTargetingDetailsCore from './LocationTargetingDetailsCore.vue';

  export default {
    components: {
      LocationTargetingDetailsCore,
    },
    props: {
      entity: {
        type: Object,
        default: null,
      },
      isSharedPlan: {
        type: Boolean,
        default: false,
      },
      isSharedPlanPreviewEnabled: {
        type: Boolean,
        default: false,
      },
      physicalScreenCount: {
        type: Number,
        default: 0,
      },
      isAudienceInfoLoading: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        isLanguageLoading: false,
        isLocationLoading: false,
        isSitesLoading: false,
        isUnitsLoading: false,
        isMarketsLoading: false,
        isMediaTypesLoading: false,
        isNetworksLoading: false,
        isGeofenceGroupsLoading: false,
        excludedCreativeOrientations: [],
        excludedCreativeFormats: [],
        excludedIabCategories: [],
        excludedLocations: [],
        excludedSites: [],
        excludedUnits: [],
        excludedMarkets: [],
        excludedMediaTypes: [],
        excludedNetworks: [],
        excludedGeofenceGroups: [],
        excludedLanguages: [],
        excludedUnitPacks: [],
        excludedUnitTags: [],
        geofenceGroups: [],
        hasAccessToTargetedGeofenceGroups: true,
        creativeOrientations: [],
        creativeFormats: [],
        iabCategories: [],
        languages: [],
        locations: [],
        markets: [],
        mediaTypes: [],
        networksQuery: useNetworks(),
        selectedNetworks: [],
        sites: [],
        targetedUnitIDs: [],
        targetedSiteIDs: [],
        units: [],
        unitPacks: [],
        unitTags: [],
        unitGeofencesToExport: null,
        siteGeofencesToExport: null,
        isAudienceWithGeofencesInfoLoading: true,
        pollGetTargetedUnitAndSiteIDsSubscription: null,
        // These next 3 variables will take the page size, page number and the total length of the object (units, sites, etc.) sent from the parent component
        // They're used to load the object in batches instead of loading everything at once.
        pageSizes: {
          type: Object,
          default: null,
        },
        pageNumbers: {
          type: Object,
          default: null,
        },
        lengths: {
          type: Object,
          default: null,
        },
        areGeofencesCreatedLessThan5Min: false,
      };
    },
    computed: {
      isLineItemOrPlan() {
        return this.entity instanceof LineItem || this.entity instanceof Plan;
      },
      isCreative() {
        return this.entity instanceof Creative;
      },
      networks() {
        return this.networksQuery.items || [];
      },
      targeting() {
        return {
          excludedCreativeOrientations: this.excludedCreativeOrientations,
          excludedCreativeFormats: this.excludedCreativeFormats,
          excludedIabCategories: this.excludedIabCategories,
          excludedLanguages: this.excludedLanguages,
          excludedLocations: this.excludedLocations,
          excludedSites: this.excludedSites,
          excludedUnits: this.excludedUnits,
          excludedMarkets: this.excludedMarkets,
          excludedMediaTypes: this.excludedMediaTypes,
          excludedNetworks: this.excludedNetworks,
          excludedGeofenceGroups: this.excludedGeofenceGroups,
          geofenceGroups: this.geofenceGroups,
          hasAccessToTargetedGeofenceGroups: this.hasAccessToTargetedGeofenceGroups,
          creativeOrientations: this.creativeOrientations,
          creativeFormats: this.creativeFormats,
          iabCategories: this.iabCategories,
          languages: this.languages,
          locations: this.locations,
          markets: this.markets,
          mediaTypes: this.mediaTypes,
          selectedNetworks: this.selectedNetworks,
          sites: this.sites,
          targetedUnitIDs: this.targetedUnitIDs,
          targetedSiteIDs: this.targetedSiteIDs,
          units: this.units,
          unitGeofencesToExport: this.unitGeofencesToExport,
          siteGeofencesToExport: this.siteGeofencesToExport,
          isAudienceInfoLoading: this.isAudienceInfoLoading,
          isAudienceWithGeofencesInfoLoading: this.isAudienceWithGeofencesInfoLoading,
          pageSizes: this.pageSizes,
          pageNumbers: this.pageNumbers,
          lengths: this.lengths,
        };
      },
    },
    methods: {
      ...mapActions('orgApp', [
        'getGeofenceGroups',
        'getTargetedUnitAndSiteIDs',
        'getLanguages',
        'getLocations',
        'getMarkets',
        'getMediaTypes',
        'getSites',
        'getGeofenceSiteMappingForSites',
        'getGeofenceUnitMappingForUnits',
        'getSitesBriefUsingPost',
        'getUnitsBriefUsingPost',
        'getUnitPacks',
        'getTags',
      ]),
      ...mapActions(['getUnits', 'getMediaTypes']),
      async getLocationBatch(page, exclusion = false) {
        this.isLocationLoading = true;
        const locationIDs = exclusion ? this.entity.locationExclusionIDs : this.entity.locationIDs;

        const ids = locationIDs.slice(page * 500, (page + 1) * 500);
        const queryParams = QueryParams.for({
          top: null,
          filterCollection: new FilterCollection([new Filter('location_id', ids, FilterComparisonOperator.IN)]),
        });

        try {
          const locations = await this.getLocations(queryParams);
          if (locations) {
            if (exclusion) {
              this.excludedLocations = [...this.excludedLocations, ...locations];
            } else {
              this.locations = [...this.locations, ...locations];
            }

            if (page < Math.floor(locationIDs.length / 500)) {
              await this.getLocationBatch(page + 1, exclusion);
            }
          }
        } catch (e) {
          // Make sure the component is still mounted before notifying the user.
          if (this.$refs.locationTargetingDetailsCore) {
            UserNotifications.somethingWentWrong(e, i18n.global.t('messages.failedToGetLocation'));
            throw e;
          }
        } finally {
          this.isLocationLoading = false;
        }
      },
      async getSiteBatch(page, exclusion = false) {
        this.isSitesLoading = true;
        const siteIDs = exclusion ? this.entity.siteExclusionIDs : this.entity.siteIDs;

        const ids = siteIDs.slice(page * 500, (page + 1) * 500);

        const queryParams = QueryParams.for({
          top: null,
          filterCollection: new FilterCollection([new Filter('site_id', ids, FilterComparisonOperator.IN)]),
        });

        try {
          const sites = await queryParams.run(this.getSites);

          if (sites) {
            if (exclusion) {
              this.excludedSites = [...this.excludedSites, ...sites.items];
            } else {
              this.sites = [...this.sites, ...sites.items];
            }

            if (page < Math.floor(siteIDs.length / 500)) {
              await this.getSiteBatch(page + 1, exclusion);
            }
          }
        } catch (e) {
          // Make sure the component is still mounted before notifying the user.
          if (this.$refs.locationTargetingDetailsCore) {
            UserNotifications.somethingWentWrong(e, i18n.global.t('messages.failedToGetLocation'));
            throw e;
          }
        } finally {
          this.isSitesLoading = false;
        }
      },
      async getUnitBatch(page, exclusion = false) {
        this.isUnitsLoading = true;
        const unitIDs = exclusion ? this.entity.unitExclusionIDs : this.entity.unitIDs;

        const ids = unitIDs.slice(page * 1500, (page + 1) * 1500);

        const queryParams = QueryParams.for({
          top: null,
          filterCollection: new FilterCollection([new Filter('unit_id', ids, FilterComparisonOperator.IN)]),
          include: ['unit_id', 'external_id', 'name'],
        });

        try {
          const units = (await this.getUnits({ queryParams }))?.items;
          if (units) {
            if (exclusion) {
              this.excludedUnits = [...this.excludedUnits, ...units];
            } else {
              this.units = [...this.units, ...units];
            }
            if (page < Math.floor(unitIDs.length / 1500)) {
              await this.getUnitBatch(page + 1, exclusion);
            }
          }
        } catch (e) {
          // Make sure the component is still mounted before notifying the user.
          if (this.$refs.locationTargetingDetailsCore) {
            UserNotifications.somethingWentWrong(e, i18n.global.t('messages.failedToGetUnits'));
            throw e;
          }
        } finally {
          this.isUnitsLoading = false;
        }
      },
      async getMarketsBatch(page, exclusion = false) {
        if (this.entity.marketIDs == null) {
          return;
        }
        this.isMarketsLoading = true;

        const marketIDs = exclusion ? this.entity.marketExclusionIDs : this.entity.marketIDs;

        const pageSize = 500;
        const ids = marketIDs.slice(page * pageSize, (page + 1) * pageSize);

        try {
          const markets = await this.getMarkets({
            queryParams: QueryParams.for({
              top: pageSize,
              filterCollection: new FilterCollection([new Filter('market_id', ids, FilterComparisonOperator.IN)]),
              include: ['market_id', 'name', 'type', 'external_id', 'location_id'],
            }),
          });

          if (markets) {
            if (exclusion) {
              this.excludedMarkets = [...this.excludedMarkets, ...markets.items];
            } else {
              this.markets = [...this.markets, ...markets.items];
            }
            if (page < Math.floor(marketIDs.length / 500)) {
              await this.getMarketsBatch(page + 1, exclusion);
            }
          }
        } catch (e) {
          // Make sure the component is still mounted before notifying the user.
          if (this.$refs.locationTargetingDetailsCore) {
            UserNotifications.somethingWentWrong(e, i18n.global.t('messages.failedToGetMarkets'));
            throw e;
          }
        } finally {
          this.isMarketsLoading = false;
        }
      },
      async getSelectedMediaTypes(exclusion = false) {
        this.isMediaTypesLoading = true;
        const mediaTypeIDs = exclusion ? this.entity.mediaTypeExclusionIDs : this.entity.mediaTypeIDs;
        try {
          const mediaTypeList = await this.getMediaTypes({
            queryParams: QueryParams.for({
              filterCollection: new FilterCollection([
                new Filter('mediatype_id', mediaTypeIDs, FilterComparisonOperator.IN),
              ]),
            }),
          });
          if (mediaTypeList != null) {
            if (exclusion) {
              this.excludedMediaTypes = mediaTypeList.items;
            } else {
              this.mediaTypes = mediaTypeList.items;
            }
          }
        } catch (e) {
          // Make sure the component is still mounted before notifying the user.
          if (this.$refs.locationTargetingDetailsCore) {
            UserNotifications.somethingWentWrong(e, i18n.global.t('messages.failedToGetMediaTypes'));
            throw e;
          }
        } finally {
          this.isMediaTypesLoading = false;
        }
      },
      getSelectedNetworks(exclusion = false) {
        this.isNetworksLoading = true;
        const networkIDs = exclusion ? this.entity.networkExclusionIDs : this.entity.networkIDs;
        try {
          const networkList = networkIDs.map(
            (id) =>
              this.networks.find((x) => x.id === id) ||
              new Network(id, `${i18n.global.t('messages.unknownID')} (#${id})`),
          );
          if (networkList != null) {
            if (exclusion) {
              this.excludedNetworks = networkList;
            } else {
              this.selectedNetworks = networkList;
            }
          }
        } catch (e) {
          // Make sure the component is still mounted before notifying the user.
          if (this.$refs.locationTargetingDetailsCore) {
            UserNotifications.somethingWentWrong(e, i18n.global.t('messages.failedToGetNetworks'));
            throw e;
          }
        } finally {
          this.isNetworksLoading = false;
        }
      },
      async getGeofenceGroupsWithGeofences(geofenceGroupIDs) {
        this.isGeofenceGroupsLoading = true;
        const queryParams = QueryParams.for({
          top: null,
          filterCollection: new FilterCollection([
            new Filter('geofencegroup_id', geofenceGroupIDs, FilterComparisonOperator.IN),
          ]),
          expand: ['geofences'],
          include: [
            'geofencegroup_id',
            'name',
            'geofences.geofence_id',
            'geofences.name',
            'geofences.description',
            'geofences.lat',
            'geofences.lng',
            'geofences.tags',
          ],
        });
        try {
          const result = await queryParams.run(this.getGeofenceGroups);
          if (result) {
            return result.items;
          }
        } catch (e) {
          // Make sure the component is still mounted before notifying the user.
          if (this.$refs.locationTargetingDetailsCore) {
            UserNotifications.somethingWentWrong(e, i18n.global.t('messages.failedToGetGeofenceGroups'));
            throw e;
          }
        } finally {
          this.isGeofenceGroupsLoading = false;
        }

        return null;
      },
      async getSelectedUnitPacks() {
        try {
          const allUnitPacks = await this.getUnitPacks({ showLoader: false });
          if (allUnitPacks) {
            const packs = allUnitPacks.items;
            if (packs?.length) {
              this.unitPacks = packs.filter((x) => this.entity.unitPackIDs.some((y) => y === x.id));
              this.excludedUnitPacks = packs.filter((x) => this.entity.unitPackExclusionIDs.some((y) => y === x.id));
            }
          }
        } catch (e) {
          // Make sure the component is still mounted before notifying the user.
          if (this.$refs.locationTargetingDetailsCore) {
            UserNotifications.somethingWentWrong(e, i18n.global.t('messages.failedToGetUnitPacks'));
            throw e;
          }
        }
      },
      async getSelectedUnitTags() {
        try {
          const allTags = await this.getTags({ showLoader: false });
          if (allTags) {
            const tags = allTags.map((x) => ({ name: x.tag }));
            if (tags?.length) {
              this.unitTags = tags.filter((x) => this.entity.unitTags.some((y) => y === x.name));
              this.excludedUnitTags = tags.filter((x) => this.entity.unitTagExclusions.some((y) => y === x.name));
            }
          }
        } catch (e) {
          // Make sure the component is still mounted before notifying the user.
          if (this.$refs.locationTargetingDetailsCore) {
            UserNotifications.somethingWentWrong(e, i18n.global.t('messages.failedToGetUnitTags'));
            throw e;
          }
        }
      },
      async exportSites() {
        const geofenceGroups = await this.getGeofenceGroupsWithGeofences(this.entity.geofenceGroupIDs);
        this.siteGeofencesToExport = await this.getGeofencesSiteExport(geofenceGroups, this.targetedSiteIDs);
        this.$refs.locationTargetingDetailsCore.$refs.geofenceSitesFileDownload.show();
      },
      async exportUnits() {
        const geofenceGroups = await this.getGeofenceGroupsWithGeofences(this.entity.geofenceGroupIDs);
        this.unitGeofencesToExport = await this.getGeofencesUnitExport(geofenceGroups, this.targetedUnitIDs);
        this.$refs.locationTargetingDetailsCore.$refs.geofenceUnitsFileDownload.show();
      },
      async getGeofencesSiteExport(geofenceGroups, siteIDs) {
        const geofencesByID = new Map();
        const geofenceSiteMapping = new Map();
        const uniqueSiteIDs = new Set();
        const siteBriefsById = new Map();
        const geofenceSiteExport = [];

        for await (const geofenceGroup of geofenceGroups) {
          geofenceGroup.geofences.forEach((geofence) => {
            geofencesByID.set(geofence.id, geofence);
          });

          const geofenceSiteMappingObj = await this.getGeofenceSiteMappingForSites({
            geofenceGroupID: geofenceGroup.id,
            siteIDs,
          });
          Object.entries(geofenceSiteMappingObj).forEach((entry) => {
            const [key, siteInfos] = entry;
            siteInfos.forEach((siteInfo) => {
              uniqueSiteIDs.add(siteInfo.site_id);
            });
            geofenceSiteMapping.set(Number(key), siteInfos);
          });
        }

        // Return only site-network links with targted networks, as sites may be linked to multiple networks
        // As well, ensure only targeted screens are included in the Screen Count
        const sitesBrief = await this.getSitesBriefUsingPost({
          siteIDs: Array.from(uniqueSiteIDs),
          networkIDs: this.entity.targetParamsEstimatedDeliveryStatistics?.unit_targeting?.network_ids,
          unitIDs: this.entity.targetParamsEstimatedDeliveryStatistics?.unit_targeting?.unit_ids,
        });
        sitesBrief.forEach((siteBrief) => {
          if (!siteBriefsById.has(siteBrief.siteID)) {
            siteBriefsById.set(siteBrief.siteID, []);
          }
          siteBriefsById.get(siteBrief.siteID).push(siteBrief);
        });

        geofenceGroups.forEach((geofenceGroup) => {
          geofenceGroup.geofences.forEach((geofence) => {
            const sitesInGeofence = geofenceSiteMapping.get(geofence.id);
            if (sitesInGeofence === undefined) {
              return;
            }
            sitesInGeofence.forEach((siteInGeofence) => {
              if (!siteBriefsById.has(siteInGeofence.site_id)) {
                return;
              }
              siteBriefsById.get(siteInGeofence.site_id).forEach((siteBrief) => {
                const newGeofenceSiteExportEntry = new GeofenceSiteExportModel(
                  geofenceGroup.name,
                  geofence.name,
                  geofence.description,
                  geofence.lat,
                  geofence.lng,
                  geofence.tags,
                  siteBrief.externalID,
                  siteBrief.name,
                  siteBrief.city,
                  siteBrief.region,
                  siteBrief.latitude,
                  siteBrief.longitude,
                  siteInGeofence.distance_from_centroid,
                  siteBrief.networkName,
                  siteBrief.screenCount,
                );
                geofenceSiteExport.push(newGeofenceSiteExportEntry);
              });
            });
          });
        });
        return geofenceSiteExport;
      },
      async getGeofencesUnitExport(geofenceGroups, unitIDs) {
        const geofencesByID = new Map();
        const geofenceUnitMapping = new Map();
        const uniqueUnitIDs = new Set();
        const unitBriefById = new Map();
        const geofenceUnitExport = [];

        for await (const geofenceGroup of geofenceGroups) {
          geofenceGroup.geofences.forEach((geofence) => {
            geofencesByID.set(geofence.id, geofence);
          });

          const geofenceUnitMappingObj = await this.getGeofenceUnitMappingForUnits({
            geofenceGroupID: geofenceGroup.id,
            unitIDs,
          });
          Object.entries(geofenceUnitMappingObj).forEach((entry) => {
            const [key, unitInfos] = entry;
            unitInfos.forEach((unitInfo) => {
              uniqueUnitIDs.add(unitInfo.unit_id);
            });
            geofenceUnitMapping.set(Number(key), unitInfos);
          });
        }

        const unitsBrief = await this.getUnitsBriefUsingPost({ unitIDs: Array.from(uniqueUnitIDs) });
        unitsBrief.forEach((unitBrief) => {
          unitBriefById.set(unitBrief.unitID, unitBrief);
        });

        geofenceGroups.forEach((geofenceGroup) => {
          geofenceGroup.geofences.forEach((geofence) => {
            const unitsInGeofence = geofenceUnitMapping.get(geofence.id);
            if (unitsInGeofence === undefined) {
              return;
            }
            unitsInGeofence.forEach((unitInGeofence) => {
              const unitBrief = unitBriefById.get(unitInGeofence.unit_id);
              if (unitBrief === undefined) {
                return;
              }
              const newGeofenceUnitExportEntry = new GeofenceUnitExportModel(
                geofenceGroup.name,
                geofence.name,
                geofence.description,
                geofence.lat,
                geofence.lng,
                geofence.tags,
                unitBrief.siteExternalID,
                unitBrief.siteName,
                unitBrief.city,
                unitBrief.region,
                unitBrief.externalID,
                unitBrief.name,
                unitBrief.latitude,
                unitBrief.longitude,
                unitInGeofence.distance_from_centroid,
                unitBrief.screenWidth,
                unitBrief.screenHeight,
                unitBrief.networkName,
                unitInGeofence.site_distance_from_centroid,
              );
              geofenceUnitExport.push(newGeofenceUnitExportEntry);
            });
          });
        });
        return geofenceUnitExport;
      },
      pollGetTargetedUnitAndSiteIDs(pollInterval, maxAttempts) {
        return defer(() =>
          this.getTargetedUnitAndSiteIDs({ data: this.entity.targetParamsEstimatedDeliveryStatistics }),
        ).pipe(pollWhile(pollInterval, (data) => data?.unitIDs?.length === 0, maxAttempts, true));
      },
      async getSelectedLanguages() {
        this.isLanguageLoading = true;
        const languageIDs = [...this.entity.languageIDs, ...this.entity.languageExclusionIDs];

        try {
          const languageList = await this.getLanguages({
            queryParams: QueryParams.for({
              filterCollection: new FilterCollection([
                new Filter('language_id', languageIDs, FilterComparisonOperator.IN),
              ]),
            }),
          });
          if (languageList) {
            const languages = languageList?.items;
            if (languages?.length) {
              this.languages = [...languages].filter((l) => this.entity.languageIDs.includes(l.id));
              this.excludedLanguages = [...languages].filter((l) => this.entity.languageExclusionIDs.includes(l.id));
            }
          }
        } catch (e) {
          // Make sure the component is still mounted before notifying the user.
          if (this.$refs.locationTargetingDetailsCore) {
            UserNotifications.somethingWentWrong(e, i18n.global.t('messages.failedToGetLanguages'));
            throw e;
          }
        } finally {
          this.isLanguageLoading = false;
        }
      },
    },
    watch: {
      units() {
        this.$emit('unitsLoaded', this.units);
      },
      excludedUnits() {
        this.$emit('excludedUnitsLoaded', this.excludedUnits);
      },
      sites() {
        this.$emit('sitesLoaded', this.sites);
      },
      excludedSites() {
        this.$emit('excludedSitesLoaded', this.excludedSites);
      },
      languages() {
        this.$emit('languagesLoaded', this.languages);
      },
      excludedLanguages() {
        this.$emit('excludedLanguagesLoaded', this.excludedLanguages);
      },
      locations() {
        this.$emit('locationsLoaded', this.locations);
      },
      excludedLocations() {
        this.$emit('excludedLocationsLoaded', this.excludedLocations);
      },
      selectedNetworks() {
        this.$emit('networksLoaded', this.selectedNetworks);
      },
      excludedNetworks() {
        this.$emit('excludedNetworksLoaded', this.excludedNetworks);
      },
      markets() {
        this.$emit('marketsLoaded', this.markets);
      },
      excludedMarkets() {
        this.$emit('excludedMarketsLoaded', this.excludedMarkets);
      },
      mediaTypes() {
        this.$emit('mediaTypesLoaded', this.mediaTypes);
      },
      excludedMediaTypes() {
        this.$emit('excludedMediaTypesLoaded', this.excludedMediaTypes);
      },
      unitPacks() {
        this.$emit('unitPacksLoaded', this.unitPacks);
      },
      excludedUnitPacks() {
        this.$emit('excludedUnitPacksLoaded', this.excludedUnitPacks);
      },
      excludedGeofenceGroups() {
        this.$emit('excludedGeofenceGroupsLoaded', this.excludedGeofenceGroups);
      },
      geofenceGroups() {
        this.$emit('geofenceGroupsLoaded', this.geofenceGroups);
      },
      creativeOrientations() {
        this.$emit('creativeOrientationsLoaded', this.creativeOrientations);
      },
      excludedCreativeOrientations() {
        this.$emit('excludedCreativeOrientationsLoaded', this.excludedCreativeOrientations);
      },
      creativeFormats() {
        this.$emit('creativeFormatsLoaded', this.creativeFormats);
      },
      excludedCreativeFormats() {
        this.$emit('excludedExcludedCreativeFormatsLoaded', this.excludedCreativeFormats);
      },
      iabCategories() {
        this.$emit('iabCategoriesLoaded', this.iabCategories);
      },
      excludedIabCategories() {
        this.$emit('excludedIabCategoriesLoaded', this.excludedIabCategories);
      },
    },
    async mounted() {
      await waitForPendingQueries([this.networksQuery]);

      //
      // Map locations
      //
      if (this.entity.locationIDs != null && this.entity.locationIDs.length > 0) {
        this.getLocationBatch(0);
      }

      if (this.entity.locationExclusionIDs != null && this.entity.locationExclusionIDs.length > 0) {
        this.getLocationBatch(0, true);
      }

      //
      // Map sites
      //
      if (this.entity.siteIDs != null && this.entity.siteIDs.length > 0) {
        this.getSiteBatch(0);
      }

      if (this.entity.siteExclusionIDs != null && this.entity.siteExclusionIDs.length > 0) {
        this.getSiteBatch(0, true);
      }

      //
      // Map units
      //
      if (this.entity.unitIDs != null && this.entity.unitIDs.length > 0) {
        this.getUnitBatch(0);
      }

      if (this.entity.unitExclusionIDs != null && this.entity.unitExclusionIDs.length > 0) {
        this.getUnitBatch(0, true);
      }

      //
      // Map geofence groups
      //
      if (this.entity.geofenceGroupIDs != null && this.entity.geofenceGroupIDs.length > 0) {
        const geofenceGroups = await this.getGeofenceGroups({
          queryParams: QueryParams.all(
            new FilterCollection([
              new Filter('geofencegroup_id', this.entity.geofenceGroupIDs, FilterComparisonOperator.IN),
            ]),
          ),
        });

        this.geofenceGroups = [...this.geofenceGroups, ...geofenceGroups.items];
        this.hasAccessToTargetedGeofenceGroups = geofenceGroups.items.length === this.entity.geofenceGroupIDs.length;
      }

      if (this.entity.geofenceGroupExclusionIDs != null && this.entity.geofenceGroupExclusionIDs.length > 0) {
        const excludedGeofenceGroups = await this.getGeofenceGroups({
          queryParams: QueryParams.all(
            new FilterCollection([
              new Filter('geofencegroup_id', this.entity.geofenceGroupExclusionIDs, FilterComparisonOperator.IN),
            ]),
          ),
        });

        this.excludedGeofenceGroups = [...this.excludedGeofenceGroups, ...excludedGeofenceGroups.items];
      }

      //
      // Map markets
      //
      if (this.entity.marketIDs != null && this.entity.marketIDs.length > 0) {
        this.getMarketsBatch(0);
      }

      if (this.entity.marketExclusionIDs != null && this.entity.marketExclusionIDs.length > 0) {
        this.getMarketsBatch(0, true);
      }

      //
      // Map mediaTypes
      //
      if (this.entity.mediaTypeIDs != null && this.entity.mediaTypeIDs.length > 0) {
        this.getSelectedMediaTypes();
      }

      if (this.entity.mediaTypeExclusionIDs != null && this.entity.mediaTypeExclusionIDs.length > 0) {
        this.getSelectedMediaTypes(true);
      }

      //
      // Map networks
      //
      if (this.entity.networkIDs != null && this.entity.networkIDs.length > 0) {
        this.getSelectedNetworks();
      }

      if (this.entity.networkExclusionIDs != null && this.entity.networkExclusionIDs.length > 0) {
        this.getSelectedNetworks(true);
      }

      //
      // Get targeted site / unit count
      //
      if (this.isLineItemOrPlan) {
        this.isAudienceWithGeofencesInfoLoading = true;
        this.areGeofencesCreatedLessThan5Min = this.geofenceGroups?.some(
          (x) => x.createdOnUtc && DateTime.local().toUTC().diff(x.createdOnUtc, ['minutes'])?.minutes < 5,
        );

        if (this.areGeofencesCreatedLessThan5Min) {
          this.$emit('targetedUnitAndSiteIDsForGeofencesLoaded', false);
        }
        const interval = this.areGeofencesCreatedLessThan5Min ? 10000 : null;
        const maxAttempts = this.areGeofencesCreatedLessThan5Min ? 30 : 1;
        this.pollGetTargetedUnitAndSiteIDsSubscription = this.pollGetTargetedUnitAndSiteIDs(
          interval,
          maxAttempts,
        ).subscribe((result) => {
          this.targetedSiteIDs = result?.siteIDs ?? [];
          this.targetedUnitIDs = result?.unitIDs ?? [];
          this.isAudienceWithGeofencesInfoLoading = false;
          if (this.areGeofencesCreatedLessThan5Min) {
            this.$emit('targetedUnitAndSiteIDsForGeofencesLoaded', true);
          }
        });
      }

      //
      // Map languages
      //
      // Creative language targeting is handled in another way, they don't use the concept of language exclusion
      if (!this.isCreative && (this.entity.languageIDs?.length || this.entity.languageExclusionIDs?.length)) {
        await this.getSelectedLanguages();
      }

      //
      // Map creative orientations
      //
      if (this.entity.creativeOrientations?.length || this.entity.creativeOrientationExclusions?.length) {
        this.creativeOrientations = [...CreativeOrientationOptions.options()].filter((l) =>
          this.entity.creativeOrientations.includes(l.id),
        );
        this.excludedCreativeOrientations = [...CreativeOrientationOptions.options()].filter((l) =>
          this.entity.creativeOrientationExclusions.includes(l.id),
        );
      }

      //
      // Map Creative formats
      //
      if (this.entity.creativeFormats?.length || this.entity.creativeFormatExclusions?.length) {
        this.creativeFormats = [...CreativeFormatOptions.options()].filter((l) =>
          this.entity.creativeFormats.includes(l.id),
        );
        this.excludedCreativeFormats = [...CreativeFormatOptions.options()].filter((l) =>
          this.entity.creativeFormatExclusions.includes(l.id),
        );
      }

      //
      // Map iab categories
      //
      if (!this.isCreative && (this.entity.iabCategories?.length || this.entity.iabCategoryExclusions?.length)) {
        this.iabCategories = [...IABCategories.options()].filter((l) => this.entity.iabCategories.includes(l.id));
        this.excludedIabCategories = [...IABCategories.options()].filter((l) =>
          this.entity.iabCategoryExclusions.includes(l.id),
        );
      }

      //
      // Map unit packs
      //
      if (this.entity.unitPackIDs?.length || this.entity.unitPackExclusionIDs?.length) {
        await this.getSelectedUnitPacks();
      }

      //
      // Map unit tags
      //
      if (this.entity.unitTags?.length || this.entity.unitTagExclusions?.length) {
        await this.getSelectedUnitTags();
      }
    },
    unmounted() {
      this.pollGetTargetedUnitAndSiteIDsSubscription?.unsubscribe();
    },
  };
</script>
